/**
 * BookDetailsPage — the universal "view a book" destination
 * (/app/books/:bookId), replacing BookDetailModal, BookQuickPreviewPopover,
 * and CoverDetailModal. Every book-click surface in the app now routes
 * here for the same book id (see lib/bookModel.js's getCanonicalBookId/
 * bookHref), so the experience is identical regardless of where a book was
 * discovered. Sections render sequentially, no tabs/sidebar — the same
 * flat composition Profile.jsx uses for its own sections.
 */

import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import AppHeader from '@/components/home/AppHeader';
import { useBookDetails } from './hooks/useBookDetails';
import { useBookCommunityData } from './hooks/useBookCommunityData';
import BookHero from './BookHero';
import BookActionsPanel from './BookActionsPanel';
import AboutSection from './AboutSection';
import PopularTagsSection from './PopularTagsSection';
import CommunityRatingSection from './CommunityRatingSection';
import CurrentlyReadingSection from './CurrentlyReadingSection';
import ReaderReviewsSection from './ReaderReviewsSection';
import CoverArtSection from './CoverArtSection';
import SimilarBooksRail from './SimilarBooksRail';

function HeroSkeleton() {
  return (
    <section className="u-container mt-10">
      <div className="flex animate-pulse flex-col items-center gap-6 sm:flex-row sm:items-start">
        <div className="h-64 w-44 shrink-0 rounded-r-md rounded-l-[4px] bg-line/60" />
        <div className="w-full max-w-md space-y-3">
          <div className="mx-auto h-8 w-2/3 rounded bg-line/60 sm:mx-0" />
          <div className="mx-auto h-4 w-1/3 rounded bg-line/50 sm:mx-0" />
          <div className="mx-auto h-4 w-1/2 rounded bg-line/50 sm:mx-0" />
        </div>
      </div>
    </section>
  );
}

function NotFound() {
  return (
    <div className="u-container py-24 text-center">
      <h1 className="font-display text-2xl font-semibold text-ink">Book not found</h1>
      <p className="mt-2 text-sm text-subtle">It may have been removed, or the link is out of date.</p>
      <Link to="/app/explore" className="mt-4 inline-block text-sm font-semibold text-coral hover:underline">
        Back to Explore
      </Link>
    </div>
  );
}

export default function BookDetailsPage() {
  const { bookId } = useParams();
  const { state } = useLocation();
  const hint = state?.hint;

  const { book, status, isShelved, shelfBook } = useBookDetails(bookId, hint);
  const community = useBookCommunityData(book);

  return (
    <div className="relative min-h-screen bg-cream" data-testid="book-details-page">
      <AppHeader />

      <main className="pb-44 md:pb-16">
        {status === 'not-found' && <NotFound />}
        {status === 'loading' && <HeroSkeleton />}

        {status === 'success' && book && (
          <>
            <section className="u-container mt-8">
              <BookHero book={book} ratingSummary={community.ratingSummary} />
              <div className="mt-5">
                <BookActionsPanel book={book} isShelved={isShelved} shelfBook={shelfBook} />
              </div>
            </section>

            <AboutSection description={book.description} />
            <PopularTagsSection tags={community.tags} />
            <CommunityRatingSection ratingSummary={community.ratingSummary} />
            <CurrentlyReadingSection currentlyReading={community.currentlyReading} />
            <CoverArtSection bookId={book.id} />
            <ReaderReviewsSection
              reviews={community.reviews}
              totalReviews={community.totalReviews}
              hasMoreReviews={community.hasMoreReviews}
              onLoadMore={community.loadMoreReviews}
              myReview={community.myReview}
              onSubmitReview={community.submitReview}
              onDeleteReview={community.removeReview}
              reviewsError={community.reviewsError}
            />
            <SimilarBooksRail book={book} />
          </>
        )}
      </main>
    </div>
  );
}
